import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment";
import {
  INVOICE_EXCEL_VERSION_LABEL,
  formatInvoiceExcelFeeHeader,
  validateInvoiceExcelV2Export,
} from "@/commonFuncion/invoiceExcelImportLayout";


// Helper chuyển đổi số cột sang chữ cái Excel (1 -> A, 2 -> B, ..., 26 -> Z, 27 -> AA, ...)
function getColumnLetter(colIndex) {
  let letter = "";
  let temp = colIndex;
  while (temp > 0) {
    const mod = (temp - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    temp = Math.floor((temp - mod) / 26);
  }
  return letter;
}

/**
 * Xuất file Excel mẫu nhập học phí gồm 2 Sheets:
 * - Sheet 1: "Danh Sách HS" (Thông tin học sinh, điểm danh, các khoản thu ở giữa; tổng hợp thanh toán đẩy về cuối; 1 cột Ghi chú duy nhất)
 * - Sheet 2: "Khoản Thu" (Danh mục toàn bộ các khoản thu áp dụng để người dùng tham chiếu tránh sai mã)
 *
 * @param {Object} options
 * @param {Array} options.students Danh sách học sinh đã chọn
 * @param {Array} options.feeColumns Danh sách các khoản thu được cấu hình trong mẫu
 * @param {Object} options.options Tùy chọn bổ sung (includeAttendance, includePreviousDebt)
 * @param {Object} options.context Thông tin ngữ cảnh (schoolName, className, month, year, turn)
 */
export async function downloadInvoiceTemplateWithItemsAtEnd({
  students = [],
  feeColumns = [],
  options = { includeAttendance: true, includePreviousDebt: true },
  context = {},
}) {
  const {
    className = "",
    month = moment().month() + 1,
    year = moment().year(),
    turn = 1,
  } = context;

  const {
    includeAttendance = true,
    includePreviousDebt = true,
  } = options;
  validateInvoiceExcelV2Export(students, feeColumns);

  const workbook = new ExcelJS.Workbook();
  workbook.creator = "OneKids";
  workbook.created = new Date();
  workbook.modified = new Date();

  // Styles chuẩn
  const borderThin = {
    top: { style: "thin", color: { argb: "FFCBD5E1" } },
    left: { style: "thin", color: { argb: "FFCBD5E1" } },
    bottom: { style: "thin", color: { argb: "FFCBD5E1" } },
    right: { style: "thin", color: { argb: "FFCBD5E1" } },
  };

  const headerFont1 = {
    name: "Times New Roman",
    size: 11,
    bold: true,
    color: { argb: "FF0F172A" },
  };

  const headerFont2 = {
    name: "Times New Roman",
    size: 10,
    bold: true,
    color: { argb: "FF334155" },
  };

  // =========================================================================
  // SHEET 1: DANH SÁCH HS & KHOẢN THU (KHOẢN THU Ở GIỮA, THANH TOÁN Ở CUỐI)
  // =========================================================================
  const sheet1 = workbook.addWorksheet("Danh Sách HS", {
    views: [{ state: "frozen", xSplit: 3, ySplit: 4 }],
    pageSetup: {
      paperSize: 9, // A4
      orientation: "landscape",
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
    },
  });

  // Row 1: Tiêu đề lớn
  const rTitle = sheet1.addRow(["DANH SÁCH HỌC PHÍ CỦA HỌC SINH (FILE NHẬP EXCEL)"]);
  rTitle.height = 28;
  rTitle.getCell(1).font = { name: "Times New Roman", size: 15, bold: true, color: { argb: "FF1E3A8A" } };
  rTitle.getCell(1).alignment = { vertical: "middle" };

  // Row 2: Tháng và Đợt thu (dùng để đối soát tại Bước 2)
  const formattedMonth = String(month).padStart(2, "0");
  const turnVal = Number(turn) || 1;
  const rNote = sheet1.addRow(["Tháng:", `${formattedMonth}/${year}`, "Đợt:", turnVal]);
  rNote.height = 20;
  rNote.getCell(1).font = { name: "Times New Roman", size: 11, bold: true, color: { argb: "FF1E3A8A" } };
  rNote.getCell(2).font = { name: "Times New Roman", size: 11, bold: true, color: { argb: "FF2563EB" } };
  rNote.getCell(3).font = { name: "Times New Roman", size: 11, bold: true, color: { argb: "FF1E3A8A" } };
  rNote.getCell(4).font = { name: "Times New Roman", size: 11, bold: true, color: { argb: "FF2563EB" } };

  // =========================================================================
  // ĐỊNH NGHĨA CẤU TRÚC CÁC KHỐI CỘT TRÊN SHEET 1:
  // 1. Cột STT (Độc lập, merge dọc từ Row 3 đến Row 4 - KHÔNG merge chung)
  // 2. Thông tin học sinh (7 cột - Row 3 merge ngang "Thông tin học sinh")
  // 3. Thông tin điểm danh (9 cột - Row 3 merge ngang "Thông tin điểm danh")
  // 4. Các cột Khoản thu (mỗi khoản thu gồm 7 cột con)
  // 5. Thông tin thanh toán (7 cột - Row 3 merge ngang "Thông tin thanh toán")
  // 6. Thông tin khác (2 cột - Ghi chú, Mô tả đợt thu - merge dọc Row 3 -> 4)
  // =========================================================================

  // 1. Cột STT riêng biệt (không merge vào Thông tin học sinh)
  const sttHeader = { title: "STT", width: 6, align: "center" };

  // 2. Nhóm Thông tin học sinh (7 cột: Mã HS -> Khối)
  const studentInfoHeaders = [
    { title: "Mã học sinh", width: 14, align: "center", required: true },
    { title: "Họ và tên", width: 24, align: "left", required: true },
    { title: "Ngày sinh", width: 12, align: "center" },
    { title: "Số điện thoại", width: 14, align: "center" },
    { title: "Biệt danh", width: 14, align: "left" },
    { title: "Lớp", width: 14, align: "left" },
    { title: "Khối", width: 14, align: "left" },
  ];

  // 3. Nhóm Điểm danh (9 cột)
  const attendanceHeaders = [
    { title: "Học T2-T6", width: 10, align: "center", num: true },
    { title: "Học T7", width: 10, align: "center", num: true },
    { title: "Học CN", width: 10, align: "center", num: true },
    { title: "Nghỉ có phép (T2-T6)", width: 15, align: "center", num: true },
    { title: "Nghỉ không phép (T2-T6)", width: 16, align: "center", num: true },
    { title: "Nghỉ có phép (T7)", width: 14, align: "center", num: true },
    { title: "Nghỉ không phép (T7)", width: 15, align: "center", num: true },
    { title: "Đón muộn", width: 11, align: "center", num: true },
    { title: "Số ngày theo lịch", width: 15, align: "center", num: true },
  ];

  // V2 keeps metadata once in the group header/catalog.
  const feeSubHeaders = [
    { title: "Đơn giá", width: 14, align: "right", key: "price", num: true },
    { title: "Đơn vị tính", width: 12, align: "center", key: "unit" },
    { title: "Số lượng", width: 10, align: "center", key: "quantity", num: true },
    { title: "Giảm giá", width: 13, align: "right", key: "discountNumber", num: true },
    { title: "Thành tiền", width: 16, align: "right", key: "amount", num: true, isAmount: true },
  ];

  // 5. Nhóm Thông tin thanh toán (6 cột - Row 3 merge ngang "Thông tin thanh toán", màu đồng nhất)
  const paymentHeaders = [
    { title: "Thiếu/ Thừa phiếu thu trước", width: 22, align: "right", num: true },
    { title: "Phải thu", width: 16, align: "right", num: true, customNumFmt: "#,##0" },
    { title: "Đã thu", width: 15, align: "right", num: true },
    { title: "Tiền mặt", width: 14, align: "right", num: true },
    { title: "Chuyển khoản", width: 14, align: "right", num: true },
    { title: "Thiếu thừa còn lại", width: 18, align: "right", num: true, customNumFmt: "#,##0;[Red](#,##0);0" },
  ];

  // 6. Nhóm Thông tin khác: Mã phiếu thu & Ghi chú (merge dọc Row 3 đến Row 4)
  const otherHeaders = [
    { title: "Mã phiếu thu", width: 22, align: "center" },
    { title: "Ghi chú", width: 26, align: "left" },
  ];

  // Toàn bộ định nghĩa cột dùng để căn lề và định dạng ô dữ liệu
  const allColumnDefs = [
    sttHeader,
    ...studentInfoHeaders,
    ...attendanceHeaders,
    ...feeColumns.flatMap(() => feeSubHeaders),
    ...paymentHeaders,
    ...otherHeaders,
  ];

  // Row 3: Header Level 1
  const r3Values = [];
  // Row 4: Header Level 2
  const r4Values = [];

  // 1. Cột STT độc lập (Row 3 có title, Row 4 rỗng để merge dọc)
  r3Values.push(sttHeader.title);
  r4Values.push("");

  // 2. Cột Thông tin học sinh (Row 3 merge ngang 7 cột)
  studentInfoHeaders.forEach((sh, sIdx) => {
    if (sIdx === 0) {
      r3Values.push("Thông tin học sinh");
    } else {
      r3Values.push(null);
    }
    r4Values.push(sh.title);
  });

  // 3. Cột Điểm danh (Row 3 merge ngang)
  attendanceHeaders.forEach((ah, aIdx) => {
    if (aIdx === 0) {
      r3Values.push("Thông tin điểm danh");
    } else {
      r3Values.push(null);
    }
    r4Values.push(ah.title);
  });

  // 4. Cột Khoản thu (Row 3 merge ngang từng khoản)
  feeColumns.forEach((fee) => {
    const feeName = formatInvoiceExcelFeeHeader(fee);
    r3Values.push(feeName);
    for (let i = 1; i < feeSubHeaders.length; i++) {
      r3Values.push(null);
    }
    feeSubHeaders.forEach((sub) => {
      r4Values.push(sub.title);
    });
  });

  // 5. Cột Thông tin thanh toán (Row 3 merge ngang)
  paymentHeaders.forEach((ph, pIdx) => {
    if (pIdx === 0) {
      r3Values.push("Thông tin thanh toán");
    } else {
      r3Values.push(null);
    }
    r4Values.push(ph.title);
  });

  // 6. Cột khác (Ghi chú, Mô tả đợt thu - Row 3 title, Row 4 rỗng để merge dọc)
  otherHeaders.forEach((oh) => {
    r3Values.push(oh.title);
    r4Values.push("");
  });

  const row3 = sheet1.addRow(r3Values);
  row3.height = 42;
  const row4 = sheet1.addRow(r4Values);
  row4.height = 24;

  const headerRow1 = 3;
  const headerRow2 = 4;

  let currentCol = 1;

  // 1. Format và merge Cột STT riêng (merge dọc từ Row 3 đến Row 4)
  sheet1.getColumn(1).width = sttHeader.width;
  const sttCell3 = row3.getCell(1);
  const sttCell4 = row4.getCell(1);
  sttCell3.font = headerFont1;
  sttCell4.font = headerFont1;
  sttCell3.alignment = { horizontal: "center", vertical: "middle" };
  sttCell4.alignment = { horizontal: "center", vertical: "middle" };
  sttCell3.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } };
  sttCell4.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE2E8F0" } };
  sttCell3.border = borderThin;
  sttCell4.border = borderThin;
  sheet1.mergeCells(headerRow1, 1, headerRow2, 1);
  currentCol = 2;

  // 2. Format và merge Thông tin học sinh (Row 3 merge ngang 7 cột)
  const stuStartCol = currentCol;
  const stuEndCol = stuStartCol + studentInfoHeaders.length - 1;
  const bgStudent = "FFE2E8F0"; // Slate 200

  sheet1.mergeCells(headerRow1, stuStartCol, headerRow1, stuEndCol);
  const stuMainCell = row3.getCell(stuStartCol);
  stuMainCell.font = { name: "Times New Roman", size: 11, bold: true, color: { argb: "FF0F172A" } };
  stuMainCell.alignment = { horizontal: "center", vertical: "middle" };

  for (let c = stuStartCol; c <= stuEndCol; c++) {
    row3.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgStudent } };
    row3.getCell(c).border = borderThin;
  }

  studentInfoHeaders.forEach((sh, sIdx) => {
    const colNum = stuStartCol + sIdx;
    sheet1.getColumn(colNum).width = sh.width;
    const subCell = row4.getCell(colNum);
    subCell.font = headerFont2;
    subCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgStudent } };
    subCell.border = borderThin;
  });

  currentCol = stuEndCol + 1;

  // 3. Format và merge Thông tin điểm danh (Row 3 merge ngang)
  const attStartCol = currentCol;
  const attEndCol = attStartCol + attendanceHeaders.length - 1;
  const bgAttendance = "FFE0F2FE"; // Sky 100 - màu xanh nhẹ nhàng tươi mới

  sheet1.mergeCells(headerRow1, attStartCol, headerRow1, attEndCol);
  const attMainCell = row3.getCell(attStartCol);
  attMainCell.font = { name: "Times New Roman", size: 11, bold: true, color: { argb: "FF0369A1" } };
  attMainCell.alignment = { horizontal: "center", vertical: "middle" };

  for (let c = attStartCol; c <= attEndCol; c++) {
    row3.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgAttendance } };
    row3.getCell(c).border = borderThin;
  }

  attendanceHeaders.forEach((ah, aIdx) => {
    const colNum = attStartCol + aIdx;
    sheet1.getColumn(colNum).width = ah.width;
    const subCell = row4.getCell(colNum);
    subCell.font = headerFont2;
    subCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgAttendance } };
    subCell.border = borderThin;
  });

  currentCol = attEndCol + 1;

  // 4. Format và merge các Khoản thu (kèm lưu vị trí các cột con để chèn công thức)
  const feePalette = [
    "FFDbeAFE", "FFDCFCE7", "FFFEF9C3", "FFFEE2E2",
    "FFF3E8FF", "FFFFEDD5", "FFE0E7FF", "FFCCFBF1",
  ];

  const feeColRefs = [];

  feeColumns.forEach((fee, fIdx) => {
    const startCol = currentCol;
    const endCol = startCol + feeSubHeaders.length - 1;
    const bgFee = feePalette[fIdx % feePalette.length];

    sheet1.mergeCells(headerRow1, startCol, headerRow1, endCol);
    const mainCell = row3.getCell(startCol);
    mainCell.font = { name: "Times New Roman", size: 11, bold: true, color: { argb: "FF0F172A" } };
    mainCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };

    for (let c = startCol; c <= endCol; c++) {
      row3.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgFee } };
      row3.getCell(c).border = borderThin;
    }

    feeSubHeaders.forEach((sub, sIdx) => {
      const colNum = startCol + sIdx;
      sheet1.getColumn(colNum).width = sub.width;
      const subCell = row4.getCell(colNum);
      subCell.font = headerFont2;
      subCell.alignment = { horizontal: "center", vertical: "middle" };
      subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgFee } };
      subCell.border = borderThin;
    });

    const offset = (key) => feeSubHeaders.findIndex((column) => column.key === key);
    feeColRefs.push({
      priceCol: getColumnLetter(startCol + offset("price")),
      qtyCol: getColumnLetter(startCol + offset("quantity")),
      discountCol: getColumnLetter(startCol + offset("discountNumber")),
      amountCol: getColumnLetter(startCol + offset("amount")),
      amountIndex: startCol + offset("amount"),
    });

    currentCol = endCol + 1;
  });

  // 5. Format và merge Thông tin thanh toán (Row 3 merge ngang 7 cột - màu đồng nhất)
  const payStartCol = currentCol;
  const payEndCol = payStartCol + paymentHeaders.length - 1;
  const bgPayment = "FFFEF3C7"; // Amber 100 - màu vàng nhạt thanh toán đồng nhất cho toàn bộ cột thanh toán

  sheet1.mergeCells(headerRow1, payStartCol, headerRow1, payEndCol);
  const payMainCell = row3.getCell(payStartCol);
  payMainCell.font = { name: "Times New Roman", size: 11, bold: true, color: { argb: "FFB45309" } };
  payMainCell.alignment = { horizontal: "center", vertical: "middle" };

  for (let c = payStartCol; c <= payEndCol; c++) {
    row3.getCell(c).fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgPayment } };
    row3.getCell(c).border = borderThin;
  }

  paymentHeaders.forEach((ph, pIdx) => {
    const colNum = payStartCol + pIdx;
    sheet1.getColumn(colNum).width = ph.width;
    const subCell = row4.getCell(colNum);
    subCell.font = headerFont2;
    subCell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    subCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgPayment } };
    subCell.border = borderThin;
  });

  const colThieuThuaTruocIndex = payStartCol;
  const colPhaiThuIndex = payStartCol + 1;
  const colDaThuIndex = payStartCol + 2;
  const colThieuThuaConLaiIndex = payStartCol + 5;

  const colThieuThuaTruocLetter = getColumnLetter(colThieuThuaTruocIndex);
  const colPhaiThuLetter = getColumnLetter(colPhaiThuIndex);
  const colDaThuLetter = getColumnLetter(colDaThuIndex);
  const colThieuThuaConLaiLetter = getColumnLetter(colThieuThuaConLaiIndex);

  currentCol = payEndCol + 1;

  // 6. Format và merge Cột khác: Ghi chú & Mô tả đợt thu (merge dọc Row 3 đến Row 4)
  otherHeaders.forEach((oh) => {
    const colNumber = currentCol;
    sheet1.getColumn(colNumber).width = oh.width;

    const cell3 = row3.getCell(colNumber);
    const cell4 = row4.getCell(colNumber);

    cell3.font = headerFont1;
    cell4.font = headerFont1;
    cell3.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell4.alignment = { horizontal: "center", vertical: "middle", wrapText: true };

    const bg = "FFE2E8F0"; // Slate 200
    cell3.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
    cell4.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
    cell3.border = borderThin;
    cell4.border = borderThin;

    sheet1.mergeCells(headerRow1, colNumber, headerRow2, colNumber);
    currentCol++;
  });

  // Điền danh sách học sinh
  const dataList = Array.isArray(students) ? students : [];

  dataList.forEach((st, idx) => {
    const hasInvoice = Boolean(st.invoiceCode && String(st.invoiceCode).trim() !== "" && String(st.invoiceCode).trim() !== "-");

    // 1. Tùy chọn điểm danh
    const arriveT2t6Val = includeAttendance ? (st.arriveT2t6 || 0) : 0;
    const arriveT7Val = includeAttendance ? (st.arriveT7 || 0) : 0;
    const arriveCnVal = includeAttendance ? (st.arriveCn || 0) : 0;
    const absentCpT2t6Val = includeAttendance ? (st.absentCpT2t6 || 0) : 0;
    const absentKpT2t6Val = includeAttendance ? (st.absentKpT2t6 || 0) : 0;
    const absentCpT7Val = includeAttendance ? (st.absentCpT7 || 0) : 0;
    const absentKpT7Val = includeAttendance ? (st.absentKpT7 || 0) : 0;
    const leaveLaterVal = includeAttendance ? (st.leaveLater || 0) : 0;
    const quantityDateVal = st.quantityDate ?? 26;

    // 2. Tài chính snapshot hoặc khởi tạo theo quy tắc tài liệu
    let beforeMoneyVal = 0;
    let collectedMoneyVal = 0;
    let cashMoneyVal = 0;
    let transferMoneyVal = 0;
    let noteVal = st.note || "";

    if (hasInvoice) {
      // Có mã phiếu: thông tin tài chính của file lấy từ snapshot rec_tuition_invoice, không tính lại từ công thức
      beforeMoneyVal = Number(st.beforeMoney) || 0;
      collectedMoneyVal = Number(st.collectedMoney) || 0;
      cashMoneyVal = Number(st.cashMoney) || 0;
      transferMoneyVal = Number(st.transferMoney) || 0;
    } else {
      // Không có mã phiếu: collectedMoney=0, cashMoney=0, transferMoney=0
      // Nếu bật includePreviousDebt, beforeMoney được kết chuyển; tắt thì 0
      beforeMoneyVal = includePreviousDebt ? (Number(st.beforeMoney) || 0) : 0;
      collectedMoneyVal = 0;
      cashMoneyVal = 0;
      transferMoneyVal = 0;
    }

    const rowValues = [
      // 1. Cột STT độc lập (1 cột)
      idx + 1, // 0: STT

      // 2. Thông tin học sinh (7 cột)
      st.studentCode || st.kidCode || "", // 1: Mã HS
      (st.fullName || st.name || "").toUpperCase(), // 2: Họ và tên
      st.birthday ? moment(st.birthday).format("DD/MM/YYYY") : "", // 3: Ngày sinh
      st.phone || st.parentPhone || "", // 4: Số điện thoại
      st.nickName || "", // 5: Biệt danh
      st.className || className || "", // 6: Lớp
      st.gradeName || "", // 7: Khối

      // 3. Thông tin điểm danh (9 cột)
      arriveT2t6Val, // 8: Học T2-T6
      arriveT7Val, // 9: Học T7
      arriveCnVal, // 10: Học CN
      absentCpT2t6Val, // 11: Nghỉ có phép (T2-T6)
      absentKpT2t6Val, // 12: Nghỉ không phép (T2-T6)
      absentCpT7Val, // 13: Nghỉ có phép (T7)
      absentKpT7Val, // 14: Nghỉ không phép (T7)
      leaveLaterVal, // 15: Đón muộn
      quantityDateVal, // 16: Số ngày theo lịch
    ];

    const studentFeeAmounts = [];

    // 4. Các cột khoản thu ở giữa
    feeColumns.forEach((fee) => {
      // Khớp khoản thu: FE ghép khoản theo revenueCode trước, rồi lấy các giá trị từ invoiceDetails của từng học sinh
      let matchedDetail = null;
      if (hasInvoice && Array.isArray(st.invoiceDetails) && st.invoiceDetails.length > 0) {
        const feeCode = String(fee.revenueCode || "").trim().toLowerCase();
        const feeIdx = String(fee.idx || "").trim().toLowerCase();
        const feeName = String(fee.name || fee.revenueName || "").trim().toLowerCase();

        // 1. Ưu tiên ghép theo revenueCode trước
        if (feeCode) {
          matchedDetail = st.invoiceDetails.find(
            (d) => String(d.revenueCode || "").trim().toLowerCase() === feeCode
          );
        }
        // 2. Nếu chưa khớp và có idx, thử theo idx
        if (!matchedDetail && feeIdx) {
          matchedDetail = st.invoiceDetails.find(
            (d) => String(d.idx || "").trim().toLowerCase() === feeIdx
          );
        }
        // 3. Fallback theo tên khoản thu
        if (!matchedDetail && feeName) {
          matchedDetail = st.invoiceDetails.find(
            (d) => String(d.revenueName || d.name || "").trim().toLowerCase() === feeName
          );
        }
      }

      let priceVal = Number(fee.price || 0);
      let unitVal = fee.unit || "Tháng";
      let qtyVal = 0;
      let discountNumVal = 0;
      let amountVal = 0;

      if (matchedDetail) {
        // Điền từ snapshot của phiếu thực tế
        priceVal = matchedDetail.price !== undefined ? Number(matchedDetail.price) : Number(fee.price || 0);
        unitVal = matchedDetail.unit || fee.unit || "Tháng";
        qtyVal = matchedDetail.quantity != null ? Number(matchedDetail.quantity) : 0;
        discountNumVal = matchedDetail.discountNumber !== undefined
          ? Number(matchedDetail.discountNumber)
          : (matchedDetail.discountMoney !== undefined ? Number(matchedDetail.discountMoney) : 0);
        amountVal = matchedDetail.amount !== undefined
          ? Number(matchedDetail.amount)
          : (matchedDetail.moneyKt !== undefined ? Number(matchedDetail.moneyKt) : (priceVal * qtyVal - discountNumVal));
      } else if (fee.isCustom) {
        // Khoản thu tự thêm mới từ dialog Bước 1: truyền số lượng, đơn giá, giảm giá đã cấu hình
        priceVal = fee.price !== undefined ? Number(fee.price) : 0;
        unitVal = fee.unit || "Buổi";
        qtyVal = fee.quantity !== undefined ? Number(fee.quantity) : 1;
        discountNumVal = fee.discountNumber !== undefined ? Number(fee.discountNumber) : 0;
        amountVal = fee.amount !== undefined ? Number(fee.amount) : (priceVal * qtyVal - discountNumVal);
      } else {
        // Khoản hệ thống chưa có trong snapshot hoặc học sinh chưa có phiếu:
        // quantity=0, discountNumber=0, amount=0
        qtyVal = 0;
        discountNumVal = 0;
        amountVal = 0;
      }

      studentFeeAmounts.push(amountVal);

      rowValues.push(priceVal); // 1: Đơn giá
      rowValues.push(unitVal); // 2: Đơn vị tính
      rowValues.push(qtyVal); // 3: Số lượng
      rowValues.push(discountNumVal); // 4: Giảm giá
      rowValues.push(amountVal); // 5: Thành tiền
    });

    // 5. Các cột Thông tin thanh toán (6 cột)
    const totalFeeAmount = studentFeeAmounts.reduce((sum, v) => sum + (Number(v) || 0), 0);
    const phaiThuVal = totalFeeAmount;
    // Chuẩn hóa theo công thức hệ thống: afterMoney = collectedMoney - sumMoney + beforeMoney
    const thieuThuaConLaiVal = collectedMoneyVal - phaiThuVal + beforeMoneyVal;

    rowValues.push(beforeMoneyVal); // Thiếu/ Thừa phiếu thu trước
    rowValues.push(phaiThuVal); // Phải thu (sẽ gán công thức tổng thành tiền)
    rowValues.push(collectedMoneyVal); // Đã thu
    rowValues.push(cashMoneyVal); // Tiền mặt
    rowValues.push(transferMoneyVal); // Chuyển khoản
    rowValues.push(thieuThuaConLaiVal); // Thiếu thừa còn lại (sẽ gán công thức)

    // 6. Các cột khác: Mã phiếu thu & Ghi chú
    rowValues.push(hasInvoice ? (st.invoiceCode || "") : ""); // Mã phiếu thu (invoiceCode)
    rowValues.push(noteVal); // Ghi chú duy nhất

    const dataRow = sheet1.addRow(rowValues);
    dataRow.height = 22;

    const rowNumber = dataRow.number;

    // 1. Chèn công thức cho các cột Thành tiền trong khoản thu: Thành tiền = Đơn giá*Số lượng - Giảm giá
    feeColRefs.forEach((ref, fIdx) => {
      const amountFormula = `${ref.priceCol}${rowNumber}*${ref.qtyCol}${rowNumber}-${ref.discountCol}${rowNumber}`;
      const cellAmount = dataRow.getCell(ref.amountIndex);
      cellAmount.value = {
        formula: amountFormula,
        result: studentFeeAmounts[fIdx] ?? 0,
      };
    });

    // 2. Chèn công thức tính Phải thu = tổng các cột Thành tiền của các khoản thu
    let phaiThuFormula = "";
    if (feeColRefs.length > 0) {
      phaiThuFormula = feeColRefs.map((ref) => `${ref.amountCol}${rowNumber}`).join("+");
    } else {
      phaiThuFormula = "0";
    }

    const cellPhaiThu = dataRow.getCell(colPhaiThuIndex);
    cellPhaiThu.value = {
      formula: phaiThuFormula,
      result: phaiThuVal,
    };
    cellPhaiThu.numFmt = "#,##0";
    cellPhaiThu.font = {
      name: "Times New Roman",
      size: 10.5,
      bold: true,
      color: { argb: "FFEA580C" },
    };

    // 3. Chèn công thức tính Thiếu thừa còn lại: afterMoney = collectedMoney - sumMoney + beforeMoney
    // Tương ứng: = Đã thu - Phải thu + Thiếu/ Thừa phiếu thu trước
    const thieuThuaConLaiFormula = `${colDaThuLetter}${rowNumber}-${colPhaiThuLetter}${rowNumber}+${colThieuThuaTruocLetter}${rowNumber}`;

    const cellThieuThuaConLai = dataRow.getCell(colThieuThuaConLaiIndex);
    cellThieuThuaConLai.value = {
      formula: thieuThuaConLaiFormula,
      result: thieuThuaConLaiVal,
    };
    cellThieuThuaConLai.numFmt = "#,##0;[Red](#,##0);0";
    cellThieuThuaConLai.font = {
      name: "Times New Roman",
      size: 10.5,
      bold: true,
      color: { argb: thieuThuaConLaiVal < 0 ? "FFDC2626" : (thieuThuaConLaiVal > 0 ? "FF16A34A" : "FF0F172A") },
    };

    dataRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const isBalanceCol = (colNumber === colPhaiThuIndex || colNumber === colThieuThuaConLaiIndex);
      if (!isBalanceCol) {
        cell.font = { name: "Times New Roman", size: 10.5 };
      }
      cell.border = borderThin;

      const colDef = allColumnDefs[colNumber - 1];
      if (colDef) {
        cell.alignment = { horizontal: colDef.align || "left", vertical: "middle" };
        if (colDef.num && !isBalanceCol) {
          cell.numFmt = colDef.customNumFmt || "#,##0";
        }
      }
    });
  });

  // Thiết lập Conditional Formatting cho 2 cột: Phải thu & Thiếu thừa còn lại
  // Khi kết quả công thức > 0: Tự động đổi màu chữ sang Xanh lá cây (#16A34A)
  // Khi kết quả công thức < 0: Tự động đổi màu chữ sang Đỏ (#DC2626)
  const maxDataRow = Math.max(4 + dataList.length, 500);
  const phaiThuRange = `${colPhaiThuLetter}5:${colPhaiThuLetter}${maxDataRow}`;
  const thieuThuaRange = `${colThieuThuaConLaiLetter}5:${colThieuThuaConLaiLetter}${maxDataRow}`;

  [phaiThuRange, thieuThuaRange].forEach((range) => {
    sheet1.addConditionalFormatting({
      ref: range,
      rules: [
        {
          type: "cellIs",
          operator: "greaterThan",
          formulae: ["0"],
          style: {
            font: { color: { argb: "FF16A34A" }, name: "Times New Roman" },
          },
        },
        {
          type: "cellIs",
          operator: "lessThan",
          formulae: ["0"],
          style: {
            font: { color: { argb: "FFDC2626" }, name: "Times New Roman" },
          },
        },
      ],
    });
  });

  // =========================================================================
  // SHEET 2: KHOẢN THU (DANH MỤC THAM CHIẾU TRÁNH SAI MÃ)
  // =========================================================================
  const sheet2 = workbook.addWorksheet("Khoản Thu", {
    pageSetup: { paperSize: 9, orientation: "portrait" },
  });

  // Tiêu đề Sheet 2
  const r2Title = sheet2.addRow(["DANH MỤC CÁC KHOẢN THU ÁP DỤNG"]);
  r2Title.height = 26;
  r2Title.getCell(1).font = { name: "Times New Roman", size: 14, bold: true, color: { argb: "FF1E3A8A" } };
  r2Title.getCell(1).alignment = { vertical: "middle" };

  const versionRow = sheet2.addRow([INVOICE_EXCEL_VERSION_LABEL, "2"]);
  versionRow.height = 32;
  versionRow.getCell(1).alignment = { vertical: "middle", wrapText: true };
  versionRow.getCell(2).alignment = { vertical: "middle" };

  // 4. Thứ tự cột: STT, Mã khoản thu, Mã định danh, Tên khoản thu, Đơn vị tính, Đơn giá mặc định, Nguồn gốc
  const sheet2Headers = [
    { title: "STT", width: 8, align: "center" },
    { title: "Mã khoản thu", width: 20, align: "center" },
    { title: "Mã định danh", width: 32, align: "center" },
    { title: "Tên khoản thu", width: 28, align: "left" },
    { title: "Đơn vị tính", width: 14, align: "center" },
    { title: "Đơn giá mặc định", width: 18, align: "right" },
    { title: "Nguồn gốc", width: 16, align: "center" },
  ];

  const r2Header = sheet2.addRow(sheet2Headers.map((h) => h.title));
  r2Header.height = 24;
  sheet2Headers.forEach((h, hIdx) => {
    sheet2.getColumn(hIdx + 1).width = h.width;
    const c = r2Header.getCell(hIdx + 1);
    c.font = headerFont1;
    c.alignment = { horizontal: "center", vertical: "middle" };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFDBEAFE" } };
    c.border = borderThin;
  });

  feeColumns.forEach((fee, fIdx) => {
    const row2Vals = [
      fIdx + 1,
      fee.revenueCode || "—",
      fee.idx || "—",
      fee.name || fee.revenueName || `Khoản thu ${fIdx + 1}`,
      fee.unit || "Tháng",
      Number(fee.price || 0),
      fee.isCustom ? "Tự thêm mới" : "Từ hệ thống",
    ];
    const dataRow2 = sheet2.addRow(row2Vals);
    dataRow2.height = 22;
    dataRow2.eachCell((cell, colNumber) => {
      cell.font = { name: "Times New Roman", size: 10.5 };
      cell.border = borderThin;
      const hDef = sheet2Headers[colNumber - 1];
      cell.alignment = { horizontal: hDef.align, vertical: "middle" };
      if (colNumber === 6) cell.numFmt = "#,##0";
    });
  });

  // Ngăn chặn chỉnh sửa ở Sheet 'Khoản Thu', chỉ cho phép đọc và sao chép mã
  await sheet2.protect("", {
    selectLockedCells: true,
    selectUnlockedCells: true,
    formatCells: false,
    formatColumns: false,
    formatRows: false,
    insertColumns: false,
    insertRows: false,
    insertHyperlinks: false,
    deleteColumns: false,
    deleteRows: false,
    sort: false,
    autoFilter: false,
    pivotTables: false,
  });

  // Xuất file kèm thời gian để tránh trùng lặp (Yêu cầu 2)
  const buffer = await workbook.xlsx.writeBuffer();
  const safeClassName = (className || "Lop_Nhom").replace(/[^a-zA-Z0-9_\u00C0-\u024F\u1EA0-\u1EF9]/g, "_");
  const timestamp = moment().format("YYYY-MM-DD_HH-mm-ss");
  const fileName = `Mau_Nhap_Hoc_Phi_${safeClassName}_T${month}_${year}_Dot${turn}_${timestamp}.xlsx`;
  saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), fileName);
}
